import { ArrowRightOutlined, WarningOutlined } from '@ant-design/icons';
import { type VincoloCardinalitaDTO } from '@framework/api-client/api/backoffice/schemas/vincoloCardinalitaDTO.ts';
import {
  getGetFrontRichiesteIdQueryKey,
  usePostFrontRichiesteIdInoltra,
} from '@framework/api-client/api/inquiries-compile/richieste-controller/richieste-controller.ts';
import { EnumRichiestaDTOStato } from '@framework/api-client/api/inquiries-compile/schemas/enumRichiestaDTOStato.ts';
import { SubmissionCardinalityLogic } from '@framework/common-web/schema/submissions/cardinality/SubmissionCardinalityLogic.ts';
import {
  generatePath,
  useHistory,
} from '@framework/ui/core/router/TypedRoute.tsx';
import { Button, type ButtonProps, Modal, message, Tooltip } from 'antd';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { queryClient } from '../../../../core/network/queryClient.ts';
import { handleSendError } from './handleSendError.tsx';

const FullHeightButton = styled(Button)`
  &&& {
    height: 100%;
  }
`;

export interface ISubmissionSendButtonProps extends ButtonProps {
  submissionId: string | null;
  text: string;
  isCompleted: boolean;
  hasPendingPayments: boolean;
  cannotSendAsDelegate: boolean;
  tooltip?: string;
  /**
   * Se a `true` il pulsante richiede la conferma prima dell'invio.
   */
  cardinalityViolations: VincoloCardinalitaDTO[] | null | undefined;
}

/**
 * Pulsante per l'invio della richiesta.
 */
export function SubmissionSendButton(props: ISubmissionSendButtonProps) {
  const {
    submissionId,
    text,
    isCompleted,
    hasPendingPayments,
    cannotSendAsDelegate,
    disabled,
    tooltip,
    cardinalityViolations,
    ...otherProps
  } = props;
  const { t } = useTranslation();

  const send = usePostFrontRichiesteIdInoltra();
  const history = useHistory();

  const tooltipText = useMemo(() => {
    if (tooltip) return tooltip;
    if (!isCompleted) {
      return t('form.form-not-completed-tooltip', 'Compilazione incompleta');
    }
    if (hasPendingPayments) {
      return t('form.form-not-payed-tooltip', 'Pagamenti mancanti');
    }
    if (cannotSendAsDelegate) {
      return t(
        'form.form-cannot-send-as-delegate-tooltip',
        "Non puoi inviare questa richiesta in quanto la delega non permette l'invio",
      );
    }
    if (cardinalityViolations?.length) {
      const message = SubmissionCardinalityLogic.getPriorityMessage(
        cardinalityViolations,
      );
      if (message) {
        return message;
      }
    }
    return undefined;
  }, [
    tooltip,
    isCompleted,
    hasPendingPayments,
    cannotSendAsDelegate,
    cardinalityViolations,
    t,
  ]);

  const handleSend = useCallback(async () => {
    if (!submissionId) return;

    const submitFn = async () => {
      try {
        const sentSubmission = await send.mutateAsync({ id: submissionId });

        message.success(
          t(
            'form.invio-effettuato-con-successo',
            'Invio effettuato con successo',
          ),
        );
        queryClient.setQueryData(
          getGetFrontRichiesteIdQueryKey(submissionId),
          () => sentSubmission,
        );

        const path =
          sentSubmission?.stato === EnumRichiestaDTOStato.IN_INVIO
            ? generatePath('/richieste/:id/risultato', { id: submissionId })
            : generatePath('/richieste/:id/inviata', { id: submissionId });
        history.push(path);
      } catch (e: unknown) {
        handleSendError(e, t);
      }
    };

    // Se la richiesta ha una violazione di warning sulla cardinalità mostriamo un alert di conferma
    if (SubmissionCardinalityLogic.hasWarning(cardinalityViolations)) {
      Modal.confirm({
        title: t('form.invio-con-violazioni-avviso-titolo', 'Attenzione'),
        content: cardinalityViolations?.map(v => (
          <p key={v.id}>{v.descrizione}</p>
        )),
        icon: <WarningOutlined />,
        onOk: submitFn,
        okText: t('form.invio-con-violazioni-avviso-ok', 'Invia comunque'),
        cancelText: t('form.invio-con-violazioni-avviso-annulla', 'Annulla'),
      });
    } else {
      await submitFn();
    }
  }, [cardinalityViolations, history, send, submissionId, t]);

  const buttonDisabled =
    !(submissionId && isCompleted) ||
    disabled ||
    hasPendingPayments ||
    cannotSendAsDelegate ||
    SubmissionCardinalityLogic.hasError(cardinalityViolations);

  return (
    <Tooltip title={tooltipText}>
      <FullHeightButton
        {...otherProps}
        type="primary"
        size="large"
        loading={send.isLoading}
        disabled={buttonDisabled}
        onClick={handleSend}
      >
        {text} <ArrowRightOutlined aria-hidden />
      </FullHeightButton>
    </Tooltip>
  );
}
